import { isValidDate, formatDate, DATETIME_FORMATS } from '@myap/ui-library/esm/date'
import { Link } from 'react-router-dom'
import { fetchApprovalRequests } from '../../actions/approvalRequests'
import {
  selectApprovalRequestStatusProps,
  filterByNeedsAction,
} from '../../selectors/approvalRequests'
import { denormalizeReshipmentsApprovalRequests } from '../../utils/normalize'
import { Error, Loader, SortableTable, DotSizeStatusLabel } from '../common'
import {
  APPROVAL_REQUEST_CONFIG,
  ARTYPE_RESHIPMENTS,
} from '../../constants/ApprovalRequestConstants'
import { NAV_LINKS } from '../../constants/NavConstants'
import { isEmpty } from '../../utils/common'

const mapStateToProps = state => {
  const { fetching, error } = selectApprovalRequestStatusProps(state)
  const approvalRequestsArr = denormalizeReshipmentsApprovalRequests(state.approvalRequests)
  return {
    visibleApprovalRequests: filterByNeedsAction(approvalRequestsArr),
    fetching,
    error,
  }
}

const ITEM_SIZE = 73
const DISPLAY_ROWS = 20

const Wrapper = ({ children }) => (
  <>
    <div className="row">
      <div className="col-xs-12">
        <h2 style={{ marginTop: '2em' }}>Reshipments Needing Action</h2>
      </div>
    </div>
    <div className="row">
      <div className="col-xs-12">{children}</div>
    </div>
  </>
)

const StandardRow = ({
  data: {
    earliestAdminDateTime,
    aiCode,
    etsApprovalStatus,
    comments,
    orgId,
    [APPROVAL_REQUEST_CONFIG[ARTYPE_RESHIPMENTS].idField]: idField,
  },
}) => (
  <tr key={idField} aria-label={`reshipment approval request - ${idField}`}>
    <td headers="examDateCol">
      {isValidDate(earliestAdminDateTime)
        ? formatDate(earliestAdminDateTime, DATETIME_FORMATS.longMonthDayYear)
        : earliestAdminDateTime}
    </td>
    <td headers="aiCodeCol">{aiCode}</td>
    <td headers="statusCol">
      <DotSizeStatusLabel status={etsApprovalStatus} />
      {comments && <div style={{ marginLeft: '15px' }}>Comment: {comments}</div>}
    </td>
    <td headers="actionCol" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
      <Link
        to={{
          pathname: NAV_LINKS.reshipment,
          search: `?orgId=${orgId}&aiCode=${aiCode}`,
        }}
      >
        View Order <span className="cb-glyph cb-east" />
      </Link>
    </td>
  </tr>
)

const virtualRow = ({ data, index }) => (
  <tr
    aria-label={`reshipment approval request - ${
      data[index][APPROVAL_REQUEST_CONFIG[ARTYPE_RESHIPMENTS].idField]
    }`}
  >
    <td headers="examDateCol">
      {isValidDate(data[index].earliestAdminDateTime)
        ? formatDate(data[index].earliestAdminDateTime, DATETIME_FORMATS.longMonthDayYear)
        : data[index].earliestAdminDateTime}
    </td>
    <td headers="aiCodeCol">{data[index].aiCode}</td>
    <td headers="statusCol">
      <DotSizeStatusLabel status={data[index].etsApprovalStatus} />
      {data[index]?.comments ? (
        <div style={{ marginLeft: '15px' }}>Comment: {data[index].comments}</div>
      ) : null}
    </td>
    <td headers="actionCol" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
      <Link
        to={{
          pathname: NAV_LINKS.reshipment,
          search: `?orgId=${data[index].orgId}&aiCode=${data[index].aiCode}`,
        }}
      >
        View Order <span className="cb-glyph cb-east" />
      </Link>
    </td>
  </tr>
)

export default connect(mapStateToProps, { fetchApprovalRequests })(
  ({ visibleApprovalRequests, fetching, error, fetchApprovalRequests }) => {
    const headerColumns = [
      {
        headerText: { title: 'Earliest Admin Date' },
        headerAttributes: { id: 'adminDateCol' },
        key: ['earliestAdminDateTime', 'aiCode'],
        sortDir: ['asc', 'asc'],
        defaultSort: true,
      },
      {
        headerText: { title: 'AI Code' },
        headerAttributes: { id: 'aiCodeCol' },
        key: ['aiCode'],
        sortDir: ['asc'],
      },
      {
        headerText: { title: 'Status' },
        headerAttributes: { id: 'statusCol' },
        key: ['statusSortOrder', 'aiCode'],
        sortDir: ['asc', 'asc'],
      },
      {
        headerText: { title: '' },
        headerAttributes: { id: 'actionCol' },
      },
    ]

    useEffect(() => {
      fetchApprovalRequests(ARTYPE_RESHIPMENTS)
    }, [])

    if (error) return <Error />

    if (fetching) return <Loader />

    if (isEmpty(visibleApprovalRequests))
      return (
        <Wrapper>
          <p>There are no reshipments which require action at this time.</p>
        </Wrapper>
      )

    return (
      <Wrapper>
        <SortableTable
          tableAttributes={{
            id: 'reshipmentsNeedingActionTable',
            summary:
              'Reshipments approval requests needing action are listed in ascending order by earliest exam date and AI code.',
            className: 'table cb-table-striped-reverse cb-no-table-border',
          }}
          data={visibleApprovalRequests}
          virtualRow={virtualRow}
          StandardRow={StandardRow}
          columns={headerColumns}
          tableCaption={{
            caption: 'A view of reshipments approval requests needing action',
            srOnly: true,
          }}
          virtualKey={(index, data) =>
            data[index][APPROVAL_REQUEST_CONFIG[ARTYPE_RESHIPMENTS].idField]
          }
          standardKey={data => data[APPROVAL_REQUEST_CONFIG[ARTYPE_RESHIPMENTS].idField]}
          itemSize={73}
          height={ITEM_SIZE * DISPLAY_ROWS}
          style={{ fontSize: '0.6rem' }}
        />
      </Wrapper>
    )
  }
)
